import { type ReactNode, useEffect } from "react";
import {
  AgenticLogicSurface,
  agenticLogicSurfaceDescriptor,
  type AgenticLogicSurfaceDescriptor,
} from "@src/features/ai/pages/agentic-logic/surface/surface";
import { AgenticLogicSurfaceContext } from "@src/features/ai/pages/agentic-logic/surface/context";
import { useAtlasChatPresentation } from "@src/features/ai/pages/agentic-logic/surface/useAtlasChatPresentation";
import { useAtlasChatRail } from "@src/features/ai/pages/agentic-logic/surface/useAtlasChatRail";
import { useDispatch } from "@src/store";
import { setActiveAgenticLogicSurface } from "@src/store/slices/global";

export const AgenticLogicSurfaceProvider = ({
  surface,
  children,
}: {
  surface: AgenticLogicSurface;
  children: ReactNode;
}) => {
  const dispatch = useDispatch();

  // Registered for the code that reads a surface from OUTSIDE this tree — the
  // chat rail, which navigates to a rule it just created but lives in the
  // layout shell. It cannot derive the surface from the pathname, because an
  // embedded surface is a dialog and dialogs are not in the URL.
  //
  // Unregistering is unmount cleanup, deliberately: the effect in
  // `LoggedInProvider` that tears the chat down runs on the location change
  // that exits the surface, and it must still see the surface it is exiting to
  // read the (namespaced) rule param out of the new location.
  // Keyed on the descriptor's serialized form, not on the surface object: an
  // embedded surface is rebuilt on every host location change, and an object
  // identity in the dep list would re-dispatch on every render.
  const descriptorKey = JSON.stringify(agenticLogicSurfaceDescriptor(surface));
  useEffect(() => {
    dispatch(
      setActiveAgenticLogicSurface(
        JSON.parse(descriptorKey) as AgenticLogicSurfaceDescriptor
      )
    );
    return () => {
      dispatch(setActiveAgenticLogicSurface(null));
    };
  }, [descriptorKey, dispatch]);

  useAtlasChatRail(surface);
  useAtlasChatPresentation(surface);
  return (
    <AgenticLogicSurfaceContext.Provider value={surface}>
      {children}
    </AgenticLogicSurfaceContext.Provider>
  );
};
